import Search from "@mui/icons-material/Search";
import { Badge, Box, IconButton } from "@mui/material";
import { Icon } from "@vds/icons";
import { Spinner } from "react-bootstrap";
import { Body, Title } from "@vds/typography";
import { Input, Table } from "antd";
import moment from "moment-timezone";
import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useWindowSize } from "../../hooks/viewportHooks";
import { breakpoints } from "../../../styles/breakpoints";
import { updateTableFilterState } from "../../../store/actions";
import {
  FILTER_KEYS,
  PAGE_IDENTIFIERS,
  PRIORITY_LEVELS,
  TABLE_DATE_FORMATS,
  TABLE_UI_TEXT
} from "../../constants/tableConstants";
import FilterComponentUI from "../../../global/components/Filters/filterComponent";
import {
  flattenValues
} from "../../../global/utility/deviceTableUtils";
import {
  applyCameraLocationFilter,
  applyCompanionLocationFilter,
  applyAlertsLocationFilter,
  applyCameraStatusFilter,
  applyGenericStatusFilter,
  applyPendingInvitesRoleFilter,
  applyPendingInvitesSiteFilter,
  handleLocationFilterChange,
  applyAllFilters,
} from "../../../global/utility/tableFilterUtils";
import NotificationBanner from "../../../layouts/NotificationBanner";
import { fetchFilteredUsers } from "../../../pages/UserGroups/userGroupsService";
import { prepareFilters } from "../Filters/filterUtils";
import "./../../../pages/Devices/devicePageStyles.css";
import "./../../../pages/Users/Users.css";

const CustomHOCTable = () => {
  return function CustomHOCTable({
    showSearch,
    columns,
    dataList,
    locale,
    handleChange,
    noFilter,
    loading,
    showTitle,
    searchTextPlaceholder,
    extraAction,
    expandable,
    expandedRowKeys,
    onExpand,
    updatedDate,
    defaultPage,
    expandByDefault,
    activeSession,
    pageIdentifier,
    pagination,
    useReduxPersistence = false,
    companionSiteId = null,
    closeFilterTrigger = false,
  }) {
    const [date, setDate] = useState(updatedDate);
    const timeZone = moment.tz.guess();

    const formattedDate = moment(date).format(TABLE_DATE_FORMATS.DATE);

    const formattedTime = moment(date).tz(timeZone).format(TABLE_DATE_FORMATS.TIME);
    const dispatch = useDispatch();
    const { width } = useWindowSize();
    const isBelowDesktop = width !== undefined && width < parseInt(breakpoints.desktop);
    
    // Get Redux state for table filters
    const tableFilterState = useSelector((state) => state?.tableFilterReducer?.[pageIdentifier]) || {
      searchTerm: "",
      filterOptions: {},
      currentPage: 1,
    };
    
    const { filterCategories, emptyFilters, filterColumns } = prepareFilters(
      columns,
      pageIdentifier
    );
    
    // Calculate base data with keys for mapping
    const baseDataWithKeys = useMemo(() => {
      if (!dataList) return [];
      return dataList?.map((item, index) => ({ ...item, key: index }));
    }, [dataList]);
    
    // For Redux pages only: Calculate filtered data synchronously to prevent flickering
    const reduxFilteredData = useMemo(() => {
      if (!useReduxPersistence || !dataList) return null;
      // Performance check for large datasets
      if (dataList?.length > 1000) {
        console.warn('Large dataset detected, consider implementing pagination or virtualization');
      }
      // Check if we have saved filters/search to apply
      const hasFilters = Object.values(tableFilterState?.filterOptions)?.some(arr => arr && arr?.length > 0);
      const hasSearch = tableFilterState?.searchTerm;
      
      if (!hasFilters && !hasSearch) {
        return null; // No filters, will use baseDataWithKeys
      }
      
      // Apply filters synchronously
      let filtered = dataList;
      
      if (hasFilters) {
        filtered = dataList.filter((record) => {
          return Object.keys(tableFilterState?.filterOptions)?.every((key) => {
            const values = tableFilterState?.filterOptions[key];
            if (!values || values?.length === 0) return true;
            
            const isPendingUsersTable = pageIdentifier === PAGE_IDENTIFIERS.PENDING_INVITES;
            
            if (!record[key] && pageIdentifier !== PAGE_IDENTIFIERS.USER_TABLE && !isPendingUsersTable) {
              return false;
            }
            
            if (values?.length && record[key]) {
              // Camera status filter
              if ((pageIdentifier === PAGE_IDENTIFIERS.CAMERAS_TAB || pageIdentifier === PAGE_IDENTIFIERS.ASSOCIATED_CAMERAS_TAB) && key === FILTER_KEYS.STATUS) {
                return applyCameraStatusFilter(record, tableFilterState?.filterOptions);
              }
              // Generic status filter
              else if (key === FILTER_KEYS.STATUS) {
                return applyGenericStatusFilter(record, tableFilterState?.filterOptions);
              }
              // Streams filter
              else if (key === FILTER_KEYS.STREAMS) {
                return record[key]?.some((item) => values?.includes(item));
              }
              // Pending invites role filter
              else if (isPendingUsersTable && key === FILTER_KEYS.ROLE) {
                return applyPendingInvitesRoleFilter(record, values);
              }
              // Pending invites site filter
              else if (isPendingUsersTable && key === FILTER_KEYS.SITE) {
                return applyPendingInvitesSiteFilter(record, values);
              }
              // Camera location filter
              else if ((pageIdentifier === PAGE_IDENTIFIERS.CAMERAS_TAB || pageIdentifier === PAGE_IDENTIFIERS.ASSOCIATED_CAMERAS_TAB) && key === FILTER_KEYS.LOCATION) {
                return applyCameraLocationFilter(record, tableFilterState.filterOptions);
              }
              // Companion location filter
              else if (pageIdentifier === PAGE_IDENTIFIERS.COMPANION_TAB && key === FILTER_KEYS.LOCATION) {
                return applyCompanionLocationFilter(record, tableFilterState.filterOptions);
              }
              // Alerts location filter
              else if (pageIdentifier === PAGE_IDENTIFIERS.ALERTS && key === FILTER_KEYS.LOCATION) {
                return applyAlertsLocationFilter(record, tableFilterState.filterOptions);
              }
              // Simple filter - direct match
              else if (tableFilterState.filterOptions[key].includes(record[key])) {
                return true;
              } else {
                return false;
              }
            }
            return true;
          });
        });
      }
      
      // Apply search synchronously
      if (hasSearch) {
        const lowerCaseVal = tableFilterState?.searchTerm?.toLowerCase();
        filtered = filtered?.filter((data) => {
          if (pageIdentifier === PAGE_IDENTIFIERS.SITES && data?.isCampus) {
            return data?.children?.map(m => m?.siteTagName?.toLowerCase())?.join(",")?.includes(lowerCaseVal) ?? false;
          }
          return flattenValues(data)?.includes(lowerCaseVal);
        });
      }
      
      return filtered?.map((item, index) => ({ ...item, key: index }));
    }, [dataList, useReduxPersistence, tableFilterState.filterOptions, tableFilterState.searchTerm, pageIdentifier]);
    
    // Use Redux filtered data if available, otherwise base data
    const initialFilteredData = reduxFilteredData || baseDataWithKeys;
    
    // Initialize states - use Redux state if available, otherwise use defaults
    const [searchTerm, setSearchTerm] = useState(
      useReduxPersistence && tableFilterState ? tableFilterState.searchTerm : ""
    );
    const [filteredData, setFilteredData] = useState(initialFilteredData);
    const [showNotification, setShowNotification] = useState(false);
    const [showFiltersModal, setShowFiltersModal] = useState(false);

    useEffect(() => {
      if (closeFilterTrigger) {
        setShowFiltersModal(false);
      }
    }, [closeFilterTrigger]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const onSelectChange = (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    };
    const rowSelection = {
      selectedRowKeys,
      onChange: onSelectChange,
    };
    
    const [selectedFilterOptions, setSelectedFilterOptions] = useState(
      useReduxPersistence && tableFilterState ? tableFilterState.filterOptions : emptyFilters
    );

    const [selectedOptions, setSelectedOptions] = useState(
      useReduxPersistence && tableFilterState ? tableFilterState.filterOptions : emptyFilters
    );
    const [expand, setExpand] = useState(expandable);
    const [currentPage, setCurrentPage] = useState(
      useReduxPersistence ? tableFilterState.currentPage : (defaultPage || 1)
    );
    const [activeFilterTab, setActiveFilterTab] = useState(
      useReduxPersistence ? tableFilterState.activeFilterTab : null
    );

    const loadingLocale = useMemo(
      () => (loading ? { emptyText: <Spinner animation="border" variant="dark" className="users-loading-spinner" /> } : locale),
      [loading, locale]
    );

    const filters = filterColumns.reduce((acc, c) => {
      acc[c.dataIndex] = c.filter || [];
      return acc;
    }, {});

    const collectKeys = (data) => {
      let keys = [];
      data?.forEach((item) => {
        if (item.children) {
          keys.push(item.key);
        }
      });
      return keys;
    };

    const handleFilterChange = (tab, option) => {
      setSelectedOptions((prevSelections) => {
        // Handle location filters using utility function
        const locationResult = handleLocationFilterChange(prevSelections, tab, option, pageIdentifier);
        if (locationResult) return locationResult;

        // Handle simple filters (non-location)
        const isSelected = prevSelections[tab]?.includes(option.value);
        return {
          ...prevSelections,
          [tab]: isSelected
            ? prevSelections[tab]?.filter((item) => item !== option.value) // Uncheck
            : [...(prevSelections[tab] || []), option.value], // Check
        };
      });
    };
    const handleApplyFilter = async () => {
      setSelectedFilterOptions(selectedOptions);
      let filteredList = dataList;
      const isPendingUsersTable = pageIdentifier === PAGE_IDENTIFIERS.PENDING_INVITES;
      
      if (pageIdentifier === PAGE_IDENTIFIERS.USER_TABLE && !isPendingUsersTable) {
        // Only make API call if role or site filters are selected
        if (selectedOptions?.role?.length || selectedOptions?.site?.length) {
          const result = await fetchFilteredUsers(
            activeSession,
            selectedOptions?.role,
            selectedOptions?.site,
            dataList
          );
          // Only update filteredList if API call returns valid data
          if (result && Array.isArray(result)) {
            filteredList = result;
          }
        }
      }
      
      // Use shared filter function
      const filteredValue = applyAllFilters(filteredList, selectedOptions, pageIdentifier) || [];
      
      if (expandByDefault && filteredValue) {
        setExpand({
          expandedRowKeys: collectKeys(filteredValue),
          expandIcon: () => null,
        });
      }
      setFilteredData(filteredValue);
      setCurrentPage(1); // Reset to page 1 when applying filters
      setShowFiltersModal(false);
    };

    const handleCancelFilter = () => {
      setSelectedOptions(selectedFilterOptions);
      setShowFiltersModal(false);
    };

    const handleResetFilter = () => {
      setSelectedOptions(emptyFilters);
      setSelectedFilterOptions(emptyFilters);
      setFilteredData(dataList?.map((item, index) => ({ ...item, key: index })));
      setCurrentPage(1); // Reset to page 1 when resetting filters
      setActiveFilterTab(filterCategories?.[0]); // Reset to first tab when resetting filters
      if (useReduxPersistence && filterCategories?.[0]) {
        dispatch(updateTableFilterState({
          pageIdentifier,
          propName: "activeFilterTab",
          value: filterCategories[0]
        }));
      }
    };

    // Restore filter state from Redux on mount (Redux pages only)
    const hasRestoredRef = useRef(false);
    useEffect(() => {
      if (useReduxPersistence && !hasRestoredRef.current && dataList?.length > 0 ) {
        hasRestoredRef.current = true;
        // Restore filter/search state from Redux
      setSelectedFilterOptions(tableFilterState?.filterOptions || {});
      setSelectedOptions(tableFilterState?.filterOptions || {});
      setSearchTerm(tableFilterState?.searchTerm || "");
      }
    }, [useReduxPersistence, dataList]);
    
    // Reset local filter state only when dataList transitions non-empty → empty (context switch)
    // Avoids false reset on initial load or refresh loading states (e.g. Sites table)
    const prevDataListLengthRef = useRef(null);
    useEffect(() => {
      const currLen = dataList?.length ?? 0;
      const prevLen = prevDataListLengthRef.current;
      if (useReduxPersistence && prevLen > 0 && currLen === 0) {
        setSelectedFilterOptions(emptyFilters);
        setSelectedOptions(emptyFilters);
        setSearchTerm("");
      }
      prevDataListLengthRef.current = currLen;
    }, [dataList, useReduxPersistence]);

    // Update filteredData when initialFilteredData changes (only for Redux pages)
    useEffect(() => {
      if (useReduxPersistence) {
        setFilteredData(initialFilteredData);
      }
    }, [initialFilteredData, useReduxPersistence, selectedFilterOptions]);
    
    // For non-Redux pages: update when dataList changes if no active filters
    useEffect(() => {
      if (!useReduxPersistence && dataList) {
        const hasActiveFilters = Object.values(selectedFilterOptions).some(arr => arr && arr.length > 0);
        if (!hasActiveFilters && !searchTerm) {
          setFilteredData(baseDataWithKeys);
        }
      }
    }, [dataList, useReduxPersistence, selectedFilterOptions, searchTerm, baseDataWithKeys]);
    
    // Persist filter state to Redux for pages using Redux persistence
    useEffect(() => {
      if (useReduxPersistence) {
        dispatch(updateTableFilterState({
          pageIdentifier,
          propName: "filterOptions",
          value: selectedFilterOptions
        }));
      }
    }, [selectedFilterOptions, pageIdentifier, useReduxPersistence]);
    
    // Persist current page to Redux for pages using Redux persistence
    useEffect(() => {
      if (useReduxPersistence) {
        dispatch(updateTableFilterState({
          pageIdentifier,
          propName: "currentPage",
          value: currentPage
        }));
      }
    }, [currentPage, pageIdentifier, useReduxPersistence]);

    // Persist search term to Redux for pages using Redux persistence
    useEffect(() => {
      if (useReduxPersistence) {
        dispatch(updateTableFilterState({
          pageIdentifier,
          propName: "searchTerm",
          value: searchTerm
        }));
      }
      return () => {
        if (useReduxPersistence) {
          dispatch(updateTableFilterState({ pageIdentifier, propName: "searchTerm", value: "" }));
        }
      };
    }, [searchTerm, pageIdentifier, useReduxPersistence]);

    const activeFiltersCount = Object.entries(selectedFilterOptions).reduce((total, [key, value]) => {
      if (!Array.isArray(value) || value.length === 0) return total;
      if (key === FILTER_KEYS.LOCATION) {
        return total + 1; // Location treated as a single logical filter group
      }
      return total + value.length;
    }, 0);

    const filterDom = (
      <div style={{ position: "relative" }}>
        {!noFilter && (
          <Badge
            badgeContent={activeFiltersCount}
            color="primary"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: 'black',
                color: 'white'
              }
            }}
          >
            <IconButton
              onClick={() => {
                setShowFiltersModal(!showFiltersModal);
              }}
            >
              <Icon name="filter" size="large" />
            </IconButton>
          </Badge>
        )}
        {showFiltersModal && filters && Object.keys(filters)?.length && (
          <FilterComponentUI
            filters={filters}
            filterCategories={filterCategories}
            selectedOptions={selectedOptions}
            handleFilterChange={handleFilterChange}
            handleApply={handleApplyFilter}
            handleCancel={handleCancelFilter}
            handleReset={handleResetFilter}
            pageIdentifier={pageIdentifier}
            useReduxPersistence={useReduxPersistence}
            activeFilterTab={activeFilterTab}
            companionSiteId={companionSiteId}
            onTabChange={(tab) => {
              setActiveFilterTab(tab);
              if (useReduxPersistence) {
                dispatch(updateTableFilterState({
                  pageIdentifier,
                  propName: "activeFilterTab",
                  value: tab
                }));
              }
            }}
          />
        )}
      </div>
    );

    const onSearch = (searchText) => {
      setSearchTerm(searchText);
      if (!searchText) {
        // When clearing search, check if there are active filters
        const hasActiveFilters = Object.values(selectedFilterOptions).some(arr => arr && arr.length > 0);
        
        if (hasActiveFilters && dataList) {
          // Re-apply filters without search using shared function
          const filtered = applyAllFilters(dataList, selectedFilterOptions, pageIdentifier);
          setFilteredData(filtered?.map((item, index) => ({ ...item, key: index })) || []);
        } else {
          setFilteredData(dataList?.map((item, index) => ({ ...item, key: index })) || []);
        }
        setCurrentPage(1); // Reset to page 1 when clearing search
        return;
      } else {
        const lowerCaseVal = searchText.toLowerCase();
        
        // Start with dataList or filtered data if filters are active
        const hasActiveFilters = Object.values(selectedFilterOptions).some(arr => arr && arr.length > 0);
        let baseData = dataList || [];
        
        // Apply filters first if they exist using shared function
        if (hasActiveFilters && dataList) {
          baseData = applyAllFilters(dataList, selectedFilterOptions, pageIdentifier);
        }
        
        // Then apply search on top of filtered data
        const filtered = baseData?.filter((data) => {
          if(pageIdentifier === PAGE_IDENTIFIERS.SITES && data?.isCampus){ 
            return data?.children?.map(m => m?.siteTagName?.toLowerCase())?.join(",")?.includes(lowerCaseVal) ?? false;
          }
          return flattenValues(data).includes(lowerCaseVal);
        });
        
        if (expandByDefault && filtered) {
          setExpand({
            expandedRowKeys: collectKeys(filtered),
            expandIcon: () => null,
          });
        }
        setFilteredData(filtered?.map((item, index) => ({ ...item, key: index })) || []);
        setCurrentPage(1); // Reset to page 1 when searching
      }
    };
    
    const handleTableChange = (pagination, filters, sorter) => {
      if (pagination?.current) {
        setCurrentPage(pagination.current);
      }
      if (handleChange) {
        handleChange(pagination, filters, sorter);
      }
    };
    return (
      <>
        {showSearch && (
          <Box sx={{ pt: 1, pb: 1, width: "100%" }}>
            <div className="hoc-table-toolbar">
              <Input
                className="hoc-search-input"
                style={{
                  borderColor: "black",
                  borderLeftWidth: 0,
                  borderTopWidth: 0,
                  borderRightWidth: 0,
                  borderRadius: 0,
                }}
                placeholder={
                  searchTextPlaceholder ? searchTextPlaceholder : TABLE_UI_TEXT.SEARCH_PLACEHOLDER
                }
                onChange={(e) => onSearch(e.target.value)}
                data-testid="devices_searchInput"
                value={searchTerm}
                prefix={<Search />}
              />
              <div className="hoc-toolbar-right">
                {extraAction && <div className="hoc-extra-action">{extraAction}</div>}
                {filterDom}
              </div>
            </div>
          </Box>
        )}

        {!showSearch && !companionSiteId && (
          <div className="d-flex justify-content-end position-relative cursor-pointer align-items-center gap-3" style={{ paddingBottom: "2px" }}>
            {extraAction && extraAction}
            {filterDom}
          </div>
        )}
        {updatedDate && (
          <div style={{ textAlign: "right", padding: "10px" }}>
            <Body
              size="small"
              color="#6F7171"
              viewport="desktop"
              primitive="span"
            >
              {TABLE_UI_TEXT.UPDATED_LABEL} {formattedTime} {formattedDate}
            </Body>
          </div>
        )}
        {/* Companion Device Info Page */}
        {showTitle && companionSiteId && !showSearch && (
          <div className="companion-title-filter-container">
            <Title size="small">{showTitle}</Title>
            <div className="companion-filter-wrapper">
              {filterDom}
            </div>
          </div>
        )}
        {/* Non-Companion Pages */}
        {showTitle && !companionSiteId && (
          <div style={{ paddingLeft: "15px" }}>
            <Title size="small">{showTitle}</Title>
          </div>
        )}
        <div
          className="sasp_custom-table-warpper"
        >
          {showNotification && (
            <div
              style={{
                position: "fixed",
                top: 0,
                zIndex: 1050,
              }}
            >
              <NotificationBanner
                notificationInfo={{
                  type: TABLE_UI_TEXT.NO_PERMISSION,
                  priority: PRIORITY_LEVELS.CRITICAL,
                }}
                setShowNotification={setShowNotification}
              />
            </div>
          )}

          <Table
            scroll={{ x: isBelowDesktop ? "max-content" : "min-content" }}
            rowSelection={columns.length > 0 && columns[0]?.showCheckBox ? rowSelection : false}
            columns={columns}
            dataSource={filteredData}
            locale={loadingLocale}
            onChange={handleTableChange}
            pagination={{ ...(pagination || {}), pageSize: 10, showSizeChanger: false, current: currentPage }}
            loading={false}
            expandable={expandable}
            expandedRowKeys={expandedRowKeys}
            onExpand={onExpand}
            showSorterTooltip={false}
          />
        </div>
      </>
    );
  };
};

export default CustomHOCTable;